import { useEffect, useState } from "react";
import { Calendar, Users, Wallet, Plane, FileText, AlertTriangle } from "lucide-react";
import api, { API } from "../lib/api";
import { getToken } from "../lib/tokenStorage";

const CS = { USD: "$", EUR: "€", GBP: "£", INR: "₹" };
const fmtBucket = (rows) => {
  if (!rows || rows.length === 0) return "—";
  return rows.map(r => `${CS[r.currency] || r.currency}${Number(r.amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`).join(" · ");
};

const monthOptions = () => {
  const out = [];
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  for (const yr of [currentYear + 1, currentYear, currentYear - 1]) {
    for (let m = 11; m >= 0; m -= 1) {
      const d = new Date(yr, m, 1);
      const iso = `${yr}-${String(m + 1).padStart(2, "0")}`;
      const label = d.toLocaleString(undefined, { month: "long", year: "numeric" });
      out.push({ iso, label });
    }
  }
  const currentIso = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}`;
  const idx = out.findIndex(o => o.iso === currentIso);
  if (idx > 0) {
    const [c] = out.splice(idx, 1);
    out.unshift(c);
  }
  return out;
};

// ---------------------------------------------------------------------------
// UnpricedTooltip — hover-over pill that surfaces WHICH specific bookings
// ended up at $0 so the partner knows exactly what to fix. Renders only when
// there's at least one unpriced booking of the given kind for this month.
// ---------------------------------------------------------------------------
function UnpricedTooltip({ items, kind }) {
  const [open, setOpen] = useState(false);
  const rows = (items || []).filter(x => x.kind === kind);
  if (rows.length === 0) return null;
  return (
    <div className="relative inline-block ml-1.5" data-testid={`pmonthly-unpriced-${kind}`}>
      <button
        type="button"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen(v => !v)}
        title={`${rows.length} booking${rows.length === 1 ? "" : "s"} still need pricing`}
        className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-amber-300 bg-amber-50 text-amber-800 rounded font-bold hover:bg-amber-100 focus:outline-none">
        <AlertTriangle size={9}/> {rows.length} unpriced
      </button>
      {open && (
        <div
          role="tooltip"
          data-testid={`pmonthly-unpriced-${kind}-tooltip`}
          className="absolute z-30 left-0 top-full mt-1 w-64 p-3 bg-white border border-[color:var(--aii-border)] rounded-md shadow-lg text-left">
          <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold mb-1.5 flex items-center gap-1">
            <AlertTriangle size={10}/> These need a price
          </div>
          <ul className="space-y-1 text-xs text-[color:var(--aii-text-1)]">
            {rows.slice(0, 8).map(r => (
              <li key={r.id} className="flex items-start justify-between gap-2 border-b border-slate-100 pb-1">
                <span className="font-medium truncate">{r.customer}</span>
                <span className="text-[10px] text-[color:var(--aii-muted)] flex-shrink-0">{r.when}</span>
              </li>
            ))}
          </ul>
          {rows.length > 8 && (
            <div className="text-[10px] text-[color:var(--aii-muted)] mt-1.5">+{rows.length - 8} more</div>
          )}
          <div className="text-[10px] text-[color:var(--aii-muted)] mt-2 italic">
            Ask your Aii Group contact to set a price — the amount will populate here.
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Partner Portal — "This month at a glance" mini-report.
 * Shows programme + weekend bookings for the selected month.
 */
export default function PartnerMonthlySummary() {
  const months = monthOptions();
  const [month, setMonth] = useState(months[0].iso);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get(`/partner-portal/monthly-summary?month=${month}`)
      .then(({ data }) => { if (mounted) setData(data); })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [month]);

  const prog = data?.program || { participants: 0, totals: [] };
  const wk = data?.weekend || { participants: 0, totals: [] };
  const unpriced = data?.unpriced || [];
  const anyBookings = prog.participants > 0 || wk.participants > 0;

  return (
    <div className="aii-card p-5" data-testid="pmonthly-tile">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-brand font-bold flex items-center gap-1">
            <Calendar size={11} /> Monthly summary
          </div>
          <h3 className="font-display font-semibold text-lg mt-0.5">
            {loading ? "…" : months.find(m => m.iso === month)?.label}
          </h3>
        </div>
        <select
          data-testid="pmonthly-picker"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="text-xs px-3 py-1.5 border border-[color:var(--aii-border)] rounded-md bg-white"
        >
          {months.map((m) => (
            <option key={m.iso} value={m.iso}>{m.label}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="border border-[color:var(--aii-border)] rounded-lg p-4 bg-sky-50" data-testid="pmonthly-program">
          <div className="text-[10px] uppercase tracking-widest text-sky-700 font-bold flex items-center gap-1 mb-2">
            <Plane size={11} /> Program bookings
          </div>
          <div className="flex items-baseline gap-2">
            <div className="text-3xl font-display font-semibold" data-testid="pmonthly-program-count">
              {loading ? "…" : prog.participants}
            </div>
            <div className="flex items-center gap-1 text-xs text-[color:var(--aii-text-2)]">
              <Users size={12} /> participant{prog.participants === 1 ? "" : "s"}
            </div>
          </div>
          <div className="mt-3 flex items-start gap-1.5 text-sm">
            <Wallet size={12} className="mt-1 text-brand" />
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)]">Amount to pay</div>
              <div className="flex items-center flex-wrap">
                <span className="font-semibold text-brand" data-testid="pmonthly-program-total">{fmtBucket(prog.totals)}</span>
                <UnpricedTooltip items={unpriced} kind="program"/>
              </div>
            </div>
          </div>
        </div>

        <div className="border border-[color:var(--aii-border)] rounded-lg p-4 bg-emerald-50" data-testid="pmonthly-weekend">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold flex items-center gap-1 mb-2">
            <Calendar size={11} /> Weekend bookings
          </div>
          <div className="flex items-baseline gap-2">
            <div className="text-3xl font-display font-semibold" data-testid="pmonthly-weekend-count">
              {loading ? "…" : wk.participants}
            </div>
            <div className="flex items-center gap-1 text-xs text-[color:var(--aii-text-2)]">
              <Users size={12} /> participant{wk.participants === 1 ? "" : "s"}
            </div>
          </div>
          <div className="mt-3 flex items-start gap-1.5 text-sm">
            <Wallet size={12} className="mt-1 text-emerald-700" />
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)]">Amount to pay</div>
              <div className="flex items-center flex-wrap">
                <span className="font-semibold text-emerald-800" data-testid="pmonthly-weekend-total">{fmtBucket(wk.totals)}</span>
                <UnpricedTooltip items={unpriced} kind="weekend"/>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* One combined invoice that lists BOTH program + weekend sections in
          a single PDF — the user asked to replace the two per-section
          buttons with a single grand-total download. Only surfaces when at
          least one booking exists this month. */}
      {anyBookings && (
        <a
          href={`${API}/partner-portal/monthly-invoice.pdf?month=${month}&kind=combined&token=${getToken()}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="pmonthly-combined-invoice"
          className="mt-4 inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-brand text-brand rounded-md hover:bg-[color:var(--aii-green-tint)] font-semibold w-full justify-center"
          title="Single consolidated PDF listing every program + weekend booking for this month with a grand total">
          <FileText size={12}/> Download combined monthly invoice
        </a>
      )}
    </div>
  );
}
